import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../helpers/shadcnUtils.helper';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-xl gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:text-neutral-70 disabled:bg-neutral-95 [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white  hover:bg-blue-40',
        destructive: 'bg-red-90 text-red-40  hover:bg-red-80',
        neutral: 'bg-neutral-95 text-neutral-70  hover:bg-neutral-95',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-blue-95 text-blue-40  hover:bg-blue-90 ',
        green: 'bg-green-98 text-green-40  hover:bg-green-95 ',
        link: 'text-blue-00 underline-offset-4 hover:underline',
        text: 'bg-transparent ',
      },
      size: {
        default: 'h-12 px-4 py-2 text-base md:px-8 md:py-2',
        sm: 'h-8  px-3 py-2 text-xs !rounded-full',
        'md-xs': 'h-6  px-2 py-1 text-sm !rounded-full',
        md: 'h-10  px-4 text-base ',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
